import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  MenuItem,
  CircularProgress
} from "@mui/material";
import { authenticationService } from "src/services";
import { get, put } from "src/services/httpClient.service";
import { useSnackbar } from "src/components/snackbar/useSnackbar";

/* =========================
   Types
========================= */
interface State {
  id: number;
  name: string;
}

interface UpdateDistrictProps {
  handleClose: () => void;
  fetchDistricts: () => void;
  districtValues: any;
}

const UpdateDistrict: React.FC<UpdateDistrictProps> = ({
  handleClose,
  fetchDistricts,
  districtValues
}) => {
  const { vendorId } = authenticationService.getVendorFranchiseIds();
  const APIURL = process.env.REACT_APP_BASE_API_URL;
  const { showSnackbar } = useSnackbar();

  const [states, setStates] = useState<State[]>([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    id: 0,
    stateId: "",
    name: "",  // Changed from 'District' to 'name'
    shortCode: "",  // Changed from 'shortCode' to 'shortCode'
    isActive: true,
    vendorId: vendorId,
    updatedBy: "system" // You can pass the current user here
  });

  /* =========================
     Fetch States (GET)
  ========================= */
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await get(
          `${APIURL}/api/miscellaneous/state/vendor/${vendorId}/findAll`
        );
        setStates(res?.data || []);
      } catch (error) {
        showSnackbar("Failed to load states", "error");
      }
    };

    fetchStates();
  }, [APIURL, vendorId, showSnackbar]);

  /* =========================
     Prefill District Data
  ========================= */
  useEffect(() => {
    if (districtValues) {
      setForm({
        id: districtValues.id,
        stateId: districtValues.stateId || "",
        name: districtValues.name || districtValues.district || "",
        shortCode: districtValues.shortCode || districtValues.shortCode || "",
        isActive: districtValues.isActive !== undefined ? districtValues.isActive : true,
        vendorId: vendorId,
        updatedBy: "system"
      });
    }
  }, [districtValues, vendorId]);

  /* =========================
     Handlers
  ========================= */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.stateId || !form.name) {
      showSnackbar("State and District Name are required", "warning");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        id: form.id,
        stateId: Number(form.stateId),
        name: form.name,
        shortCode: form.shortCode || null,
        isActive: form.isActive,
        vendorId: form.vendorId,
        updatedBy: form.updatedBy
      };

      const response = await put(
        `${APIURL}/api/miscellaneous/district`,
        payload
      );

      if (response?.statusCode === 200 || response?.statusCode === "200") {
        showSnackbar(
          response?.message || "District updated successfully",
          "success"
        );
        fetchDistricts();
        handleClose();
      } else {
        showSnackbar(
          response?.message || "Failed to update District",
          "error"
        );
      }
    } catch (error: any) {
      showSnackbar(error?.message || "Failed to update District", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h6" mb={2}>
        Update District
      </Typography>

      <Grid container spacing={2}>
        {/* State Dropdown */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            required
            label="State"
            name="stateId"
            value={form.stateId}
            onChange={handleChange}
          >
            {states.map(state => (
              <MenuItem key={state.id} value={state.id}>
                {state.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* District Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="District Name"
            name="name"
            value={form.name}
            onChange={handleChange}
          />
        </Grid>

        {/* Short Code */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Short Code (Optional)"
            name="shortCode"
            value={form.shortCode}
            onChange={handleChange}
          />
        </Grid>

        {/* Active Switch */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    isActive: e.target.checked
                  }))
                }
              />
            }
            label="Is Active"
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
           {loading ? "Updating..." : "Update"} 
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UpdateDistrict;